import React, { useEffect, useState } from "react";
import { Form, Input, Upload, Image } from "antd";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import LoadableButton from "../../../components/button/LoadableButton.jsx";
import { setUserDetails } from "../../../store/Slices/userSlice.js";
import ModalComponent from "../../../components/modals/ModalComponent.jsx";
import Add from "../../../assets/Add.jsx";
import { AddBannerApi, UpdateBannerApi } from "../../../api/request/banner.js";

const AddBanner = ({
  selectedBanner,
  getTemplateList,
  setMode,
  setSelectedBanner,
  setIsModalOpen,
  isModalOpen,
  mode,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.userDetails);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: "",
    image: "",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const handleFieldsChange = (_, allFields) => {
    const isChanged = allFields.some((field) => field.touched);
    setIsFormChanged(isChanged);
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ image: newFileList });
    if (newFileList?.length == 0) {
      form.setFieldsValue({ image: [] });
    }
  };

  const UploadButton = (
    <button type="button" className="flex flex-col items-center gap-2">
      <div className="size-7">
        <img src="/assets/icons/upload.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  const getBannerDetails = () => {
    if (selectedBanner.image) {
      setFileList([
        {
          uid: selectedBanner.id,
          url: selectedBanner.image,
          name: selectedBanner.name,
          status: "done",
        },
      ]);
    }
    setInitialValues({
      name: selectedBanner.name,
      image: selectedBanner.image,
    });
    form.setFieldsValue({
      name: selectedBanner.name,
      image: selectedBanner.image,
    });
  };

  const handleSubmit = async (values) => {
    setIsProfileLoading(true);
    try {
      let apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const params = {
        name: values.name,
        image: values?.image[0]?.originFileObj,
      };
      if (mode == "edit") {
        params.banner_id = selectedBanner.id;
      }

      const { data } =
        mode == "edit"
          ? await UpdateBannerApi(params, apiHeader)
          : await AddBannerApi(params, apiHeader);

      if (data?.status) {
        dispatch(
          setUserDetails({
            ...userDetails,
            name: data?.data?.name,
            image: data?.data?.image,
          })
        );
        toast.success(data?.message);
        setInitialValues({
          name: data?.data?.name,
          image: data?.data?.image,
        });
        if (data?.data?.image) {
          setFileList([{ uid: "1", url: data?.data?.image, status: "done" }]);
        }
        setIsModalOpen(false);
        getTemplateList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      form.resetFields();
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    form?.resetFields();
    if (mode == "edit") {
      getBannerDetails();
    }
  }, [mode, selectedBanner]);

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
      setInitialValues({});
      setFileList([]);
    }
  }, [isModalOpen]);

  return (
    <div>
      <button
        className="bg-secondary text-White flex items-center gap-2 rounded-lg py-2 px-4"
        onClick={() => {
          setSelectedBanner({});
          setMode("add");
          setIsModalOpen(true);
        }}
      >
        <Add />
        <span>Add Banner</span>
      </button>
      <ModalComponent
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={mode === "add" ? "Add Banner" : "Update Banner"}
        width={600}
      >
        <Form
          form={form}
          autoComplete="off"
          initialValues={initialValues}
          onFinish={handleSubmit}
          onFieldsChange={handleFieldsChange}
        >
          <div>
            <Form.Item
              name="image"
              rules={[
                { required: true, message: "Please upload banner picture." },
              ]}
            >
              <Upload
                beforeUpload={() => false}
                multiple
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
              >
                {fileList.length >= 1 ? null : UploadButton}
              </Upload>
            </Form.Item>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </div>
          <div>
            <label className="text-base font-medium">Description</label>
            <Form.Item
              name="name"
              className="mb-0"
              rules={[{ required: true, message: "Please enter description." }]}
            >
              <Input
                placeholder="Description"
                className="mt-1 w-full ps-4 py-2"
              />
            </Form.Item>
          </div>

          <LoadableButton
            className="bg-primary text-white uppercase text-base font-semibold py-2 rounded-lg px-8 mt-5"
            type="submit"
            lable={mode === "add" ? "Add Banner" : "Update Banner"}
            disabled={!isFormChanged || isProfileLoading}
            isLoading={isProfileLoading}
            loadingLable="Updating banner..."
          />
        </Form>
      </ModalComponent>
    </div>
  );
};

export default AddBanner;
